/**
 * Static fallback for EVM chain metadata (RPC, MetaMask add-network, native coin keys).
 * Per-wallet values from linked-wallets API take precedence — see linkedWallet.utils.
 */

const toHexChainId = (chainId) => `0x${Number(chainId).toString(16)}`;

const EVM_CHAINS = {
  80002: {
    chainId: 80002,
    chainName: 'Polygon Amoy Testnet',
    nativeCurrency: { name: 'POL', symbol: 'POL', decimals: 18 },
    rpcUrls: [
      'https://polygon-amoy-bor-rpc.publicnode.com',
      'https://polygon-amoy.drpc.org',
      'https://rpc-amoy.polygon.technology/',
    ],
    readRpcUrls: [
      'https://polygon-amoy-bor-rpc.publicnode.com',
      'https://polygon-amoy.drpc.org',
      'https://rpc.ankr.com/polygon_amoy',
      'https://endpoints.omniatech.io/v1/matic/amoy/public',
      'https://rpc-amoy.polygon.technology/',
    ],
    blockExplorerUrls: ['https://amoy.polygonscan.com/'],
    minPriorityFeeWei: '25000000000',
    nativeCoinKeys: ['tpolygon'],
  },
  11155111: {
    chainId: 11155111,
    chainName: 'Ethereum Sepolia',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrls: [
      'https://ethereum-sepolia-rpc.publicnode.com',
      'https://rpc.sepolia.org',
      'https://1rpc.io/sepolia',
    ],
    readRpcUrls: [
      'https://ethereum-sepolia-rpc.publicnode.com',
      'https://rpc.sepolia.org',
      'https://1rpc.io/sepolia',
      'https://rpc2.sepolia.org',
    ],
    blockExplorerUrls: ['https://sepolia.etherscan.io/'],
    minPriorityFeeWei: '1000000000',
    nativeCoinKeys: ['teth', 'tethereum'],
  },
  84532: {
    chainId: 84532,
    chainName: 'Base Sepolia',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrls: [
      'https://sepolia.base.org',
      'https://base-sepolia-rpc.publicnode.com',
    ],
    readRpcUrls: [
      'https://sepolia.base.org',
      'https://base-sepolia-rpc.publicnode.com',
      'https://base-sepolia.gateway.tenderly.co',
    ],
    blockExplorerUrls: ['https://sepolia.basescan.org/'],
    minPriorityFeeWei: '1000000000',
    nativeCoinKeys: ['eth', 'teth', 'tbaseeth', 'baseeth'],
  },
  137: {
    chainId: 137,
    chainName: 'Polygon Mainnet',
    nativeCurrency: { name: 'POL', symbol: 'POL', decimals: 18 },
    rpcUrls: [
      'https://polygon-bor-rpc.publicnode.com',
      'https://polygon-rpc.com',
    ],
    readRpcUrls: [
      'https://polygon-bor-rpc.publicnode.com',
      'https://polygon-rpc.com',
      'https://rpc.ankr.com/polygon',
    ],
    blockExplorerUrls: ['https://polygonscan.com/'],
    minPriorityFeeWei: '30000000000',
    nativeCoinKeys: ['polygon', 'matic'],
  },
  1: {
    chainId: 1,
    chainName: 'Ethereum Mainnet',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrls: [
      'https://ethereum-rpc.publicnode.com',
      'https://cloudflare-eth.com',
    ],
    readRpcUrls: [
      'https://ethereum-rpc.publicnode.com',
      'https://cloudflare-eth.com',
      'https://rpc.ankr.com/eth',
    ],
    blockExplorerUrls: ['https://etherscan.io/'],
    minPriorityFeeWei: '1000000000',
    nativeCoinKeys: ['eth', 'ethereum'],
  },
};

const coinKeyToChainId = Object.values(EVM_CHAINS).reduce((map, chain) => {
  (chain.nativeCoinKeys ?? []).forEach((key) => {
    map[String(key).toLowerCase()] = chain.chainId;
  });
  return map;
}, {});

const NATIVE_COIN_KEYS = new Set(Object.keys(coinKeyToChainId));

/** True when the symbol is a known chain-native coin (e.g. tpolygon, teth), not a generic ERC-20 like abs1. */
export const isKnownNativeCoinKey = (coinKey) => {
  const key = String(coinKey ?? '').trim().toLowerCase();
  return key ? NATIVE_COIN_KEYS.has(key) : false;
};

export const parseEvmChainId = (value) => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const getEvmChain = (chainId) => {
  const id = parseEvmChainId(chainId);
  return id != null ? EVM_CHAINS[id] ?? null : null;
};

export const isSupportedEvmChain = (chainId) => Boolean(getEvmChain(chainId));

export const resolveChainIdFromCoin = (coinKey) => {
  const key = String(coinKey ?? '').trim().toLowerCase();
  if (!key) return null;
  if (coinKeyToChainId[key] != null) return coinKeyToChainId[key];

  const prefix = key.includes(':') ? key.split(':')[0] : null;
  if (prefix && coinKeyToChainId[prefix] != null) return coinKeyToChainId[prefix];

  return null;
};

export const getMetaMaskChainParams = (chainId) => {
  const chain = getEvmChain(chainId);
  if (!chain) return null;

  const rpcUrls = [
    ...(chain.readRpcUrls ?? []),
    ...(chain.rpcUrls ?? []),
  ].filter((url, index, arr) => url && arr.indexOf(url) === index);

  return {
    chainId: toHexChainId(chain.chainId),
    chainName: chain.chainName,
    nativeCurrency: chain.nativeCurrency,
    rpcUrls,
    blockExplorerUrls: chain.blockExplorerUrls ?? [],
  };
};

export const getChainReadRpcUrls = (chainId) => {
  const chain = getEvmChain(chainId);
  if (!chain) return [];
  if (Array.isArray(chain.readRpcUrls) && chain.readRpcUrls.length) {
    return chain.readRpcUrls;
  }
  return chain.rpcUrls ?? [];
};

export const getChainMinPriorityFeeWei = (chainId) =>
  getEvmChain(chainId)?.minPriorityFeeWei ?? null;

export const getPreferredChainRpcUrl = (chainId) =>
  getChainReadRpcUrls(chainId)[0] ?? getEvmChain(chainId)?.rpcUrls?.[0] ?? null;

export const formatUnsupportedChainMessage = (chainId) => {
  const chain = getEvmChain(chainId);
  const label = chain?.chainName ?? `chain ${chainId}`;
  return `Network ${label} is not supported for non-custodial trading. Ensure the linked-wallets API returns settlement.chainId and settlement.rpcUrl.`;
};

export const formatChainConfigurationError = () =>
  'Wallet network is not configured. Ensure the linked-wallets API returns settlement.chainId and settlement.spenderAddress for non-custodial wallets.';

export const formatRateLimitError = (chainId) => {
  const chain = getEvmChain(chainId);
  const networkLabel = chain?.chainName ?? 'the selected network';
  const rpcHint = getPreferredChainRpcUrl(chainId);
  const rpcSuffix = rpcHint
    ? ` Set MetaMask → ${networkLabel} → RPC URL → ${rpcHint}`
    : '';
  return `MetaMask RPC rate limit reached. Wait 30–60 seconds, then try again.${rpcSuffix}`;
};
